import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as SharedStyle from '../shared-style';

const modalStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 999,
};

const modalContentStyle = {
  backgroundColor: '#fff',
  padding: '20px',
  borderRadius: '5px',
  maxWidth: '800px',
  width: '90%',
  maxHeight: '90%',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '15px',
};

const titleStyle = {
  margin: 0,
  fontWeight: 'bold',
  fontSize: '18px',
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '20px',
  cursor: 'pointer',
};

const canvasContainerStyle = {
  position: 'relative',
  width: '100%',
  height: '70vh',
  minHeight: '400px',
  backgroundColor: '#f5f5f5',
  overflow: 'auto',
  margin: '10px 0',
  border: '1px solid #ddd',
  borderRadius: '4px',
  boxSizing: 'border-box',
};

const controlsStyle = {
  marginTop: '15px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
};

const buttonStyle = {
  padding: '8px 15px',
  borderRadius: '4px',
  cursor: 'pointer',
  border: 'none',
  backgroundColor: SharedStyle.SECONDARY_COLOR.main,
  color: SharedStyle.COLORS.white,
};

const bubbleStyle = {
  position: 'absolute',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'move',
  color: SharedStyle.COLORS.white,
  fontWeight: 'bold',
  userSelect: 'none',
  boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
};

class Bubble {
  constructor(id, roomName, x, y, size, color) {
    this.id = id;
    this.roomName = roomName;
    this.x = x;
    this.y = y;
    this.size = size;
    this.color = color;
    this.displayName = roomName.replace(/_/g, ' ').replace(/^./, str => str.toUpperCase());
  }

  static createFromGrid(id, roomName, gridPosition, color) {
    const size = Math.min(70, gridPosition.cellWidth * 0.7, gridPosition.cellHeight * 0.7);
    return new Bubble(id, roomName, gridPosition.x, gridPosition.y, size, color);
  }

  static createFromExisting(id, roomName, existingBubble, color) {
    return new Bubble(
      id,
      roomName,
      existingBubble.x,
      existingBubble.y,
      existingBubble.size || 70,
      color
    );
  }
}

export default class BubbleChartModal extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      bubbles: [],
      connections: [],
      activeBubble: null,
      isDrawingConnection: false,
      isDeletingConnection: false,
      startBubble: null,
      // mouse position for connection drawing
      mouseX: 0,
      mouseY: 0,
      // initial bubble position for dragging
      initialMouseX: 0, // Track initial mouse position
      initialMouseY: 0  // Track initial mouse position
    };
    
    this.canvasRef = React.createRef();
  }

  componentDidMount() {
    // Initialize when component mounts
    this.initBubblesFromRoomCounts();
  }
  
  componentDidUpdate(prevProps, prevState) {
    // If modal was just shown or roomCounts changed
    if ((!prevProps.show && this.props.show) || 
        JSON.stringify(prevProps.roomCounts) !== JSON.stringify(this.props.roomCounts)) {
      // Initialize with a short delay to ensure canvas is ready
      setTimeout(() => {
        this.initBubblesFromRoomCounts();
        // Reset the connection modes when the modal is shown
        this.setState({
          isDrawingConnection: false,
          isDeletingConnection: false,
          startBubble: null
        });
      }, 200);
    }
    
    // If bubbles were updated, adjust modal size
    if (prevState.bubbles !== this.state.bubbles) {
      this.adjustModalSize();
    }
  }
  
  componentWillUnmount() {
    // Clean up event listeners
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }
  
  // New method to handle room count calculation and initialization
  initBubblesFromRoomCounts = () => {
    const { roomCounts, existingRelationships } = this.props;
    
    // Calculate total rooms based on the roomCounts object
    let roomFlatList = [];
    
    // Process each room type from the roomCounts object
    if (roomCounts) {
      console.log('Room counts from props:', roomCounts);
      
      // For each room type in roomCounts
      Object.entries(roomCounts).forEach(([key, count]) => {        
        // Add the appropriate number of each room type
        for (let i = 0; i < count; i++) {
          roomFlatList.push(`${key}_${i + 1}`);
        }
      });
    }
    
    console.log('Total rooms to create:', roomFlatList.length);
    console.log('Room list:', roomFlatList);
    
    // Initialize the bubbles with the calculated total
    this.initBubbles(roomFlatList, existingRelationships);
  };
  
  initBubbles = (roomFlatList = [], existingRelationships = null) => {
    const { translator } = this.props;
    const roomCount = roomFlatList.length;
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#d35400', '#34495e'];
    
    if (existingRelationships && 
        existingRelationships.bubbles && 
        existingRelationships.bubbles.length > 0) {
      
      console.log('Using saved bubble positions:', existingRelationships.bubbles);
      
      let bubbles = [];
      
      for (let i = 0; i < roomCount; i++) {
        const roomName = roomFlatList[i];
        const existingBubble = existingRelationships.bubbles.find(
          b => b.roomName === roomName
        );
        
        if (existingBubble) {
          bubbles.push(Bubble.createFromExisting(i + 1, roomName, existingBubble, colors[i % colors.length]));
        } else {
          const gridPosition = this.calculateGridPosition(i, roomCount);
          bubbles.push(Bubble.createFromGrid(i + 1, roomName, gridPosition, colors[i % colors.length]));
        }
      }
      
      this.setState({ 
        bubbles,
        connections: existingRelationships.connections || []
      });
      return;
    }
    
    // If no existing relationships, proceed with grid layout
    // Use fixed dimensions that work well with most screen sizes
    const containerWidth = 700;
    const containerHeight = 500;
    const margin = 50;
    
    // Calculate usable area for bubbles
    const usableWidth = containerWidth - (margin * 2);
    const usableHeight = containerHeight - (margin * 2);
    
    console.log('Using dimensions for bubble layout:', usableWidth, usableHeight);
    
    // Calculate grid dimensions based on room count
    const cols = Math.ceil(Math.sqrt(roomCount));
    const rows = Math.ceil(roomCount / cols);
    
    const cellWidth = usableWidth / cols;
    const cellHeight = usableHeight / rows;
    
    let bubbles = [];
    for (let i = 0; i < roomCount; i++) {
      const roomTypeName = roomFlatList[i];
      const gridPosition = {
        x: margin + (i % cols * cellWidth) + (cellWidth / 2),
        y: margin + (Math.floor(i / cols) * cellHeight) + (cellHeight / 2),
        cellWidth,
        cellHeight
      };
      
      bubbles.push(Bubble.createFromGrid(i + 1, roomTypeName, gridPosition, colors[i % colors.length]));
    }
    
    this.setState({ bubbles });
  };
  
  // Helper method to calculate default grid position for a bubble
  calculateGridPosition = (index, totalCount) => {
    const containerWidth = 700;
    const containerHeight = 500;
    const margin = 50;
    
    // Calculate usable area for bubbles
    const usableWidth = containerWidth - (margin * 2);
    const usableHeight = containerHeight - (margin * 2);
    
    // Calculate grid dimensions based on room count
    const cols = Math.ceil(Math.sqrt(totalCount));
    const rows = Math.ceil(totalCount / cols);
    
    const cellWidth = usableWidth / cols;
    const cellHeight = usableHeight / rows;
    
    // Calculate position in grid
    const col = index % cols;
    const row = Math.floor(index / cols);
    
    // Center bubble in its grid cell
    const x = margin + (col * cellWidth) + (cellWidth / 2);
    const y = margin + (row * cellHeight) + (cellHeight / 2);
    
    return { x, y, cellWidth, cellHeight };
  };
  
  handleMouseDown = (event, bubbleId) => {
    // Prevent default to avoid text selection
    event.preventDefault();
    
    // Only allow dragging when not in connection drawing or deleting mode
    if (!this.state.isDrawingConnection && !this.state.isDeletingConnection) {
      // Get canvas bounds
      const canvas = this.canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      // Start dragging and store initial mouse position
      this.setState({
        activeBubble: bubbleId,
        initialMouseX: x,
        initialMouseY: y
      });
    }
  };
  
  handleMouseMove = (event) => {
    const { activeBubble, bubbles, isDrawingConnection, initialMouseX, initialMouseY } = this.state;
    
    // Get canvas bounds
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    if (activeBubble !== null) {
      // Calculate distance moved from initial position
      const deltaX = Math.abs(x - initialMouseX);
      const deltaY = Math.abs(y - initialMouseY);
      
      // Only update bubble position if mouse has moved a minimum distance (to prevent accidental moves)
      if (deltaX > 3 || deltaY > 3) {
        // Update bubble position when dragging
        const updatedBubbles = bubbles.map(bubble => {
          if (bubble.id === activeBubble) {
            return {
              ...bubble,
              x,
              y,
            };
          }
          return bubble;
        });
        
        this.setState({ bubbles: updatedBubbles });
      }
    }
    
    // Always update mouse position when in drawing mode
    if (isDrawingConnection) {
      this.setState({ 
        mouseX: x,
        mouseY: y
      });
    }
  };
  
  handleMouseUp = () => {
    // Only reset activeBubble (for dragging)
    this.setState({
      activeBubble: null
    });
  };
  
  handleBubbleHover = (bubbleId) => {
    if (this.state.isDrawingConnection) {
      this.endBubble = bubbleId;
    }
  };
  
  handleBubbleMouseUp = () => {
    // Only allow dragging when not in connection drawing or deleting mode
    if (!this.state.isDrawingConnection && !this.state.isDeletingConnection) {
      // Start dragging
      this.setState({
        activeBubble: null,
      });
    }
  };

  handleBubbleClick = (event, bubbleRoomName) => {
    const { isDrawingConnection, startBubble } = this.state;
    
    // Prevent default to avoid unwanted behavior
    event.preventDefault();
    event.stopPropagation();
    
    if (isDrawingConnection) {
      if (startBubble === null) {
        // First bubble in connection
        console.log('First bubble selected:', bubbleRoomName);
        this.setState({ startBubble: bubbleRoomName });
      } else if (startBubble !== bubbleRoomName) {
        // Second bubble in connection - complete the connection
        console.log('Second bubble selected:', bubbleRoomName, 'completing connection');
        this.addConnection(startBubble, bubbleRoomName);
        this.resetDrawingState();
      }
    }
  };
  
  addConnection = (sourceRoomName, targetRoomName) => {
    const { connections } = this.state;
    
    // Check if connection already exists
    const connectionExists = connections.some(
      conn => (conn.source === sourceRoomName && conn.target === targetRoomName) || 
             (conn.source === targetRoomName && conn.target === sourceRoomName)
    );
    
    if (!connectionExists) {
      // Add the new connection
      const newConnections = [...connections, { source: sourceRoomName, target: targetRoomName }];
      
      this.setState({
        connections: newConnections,
        // We no longer reset drawing mode here, so users can make multiple connections
      });
    }
  };
  
  handleConnectionClick = (index) => {
    if (this.state.isDeletingConnection) {
      this.removeConnectionByIndex(index);
    }
  };
  
  removeConnectionByIndex = (index) => {
    const { connections } = this.state;
    
    // Create new array without the connection at the specified index
    const updatedConnections = [
      ...connections.slice(0, index),
      ...connections.slice(index + 1)
    ];
    
    this.setState({ 
      connections: updatedConnections,
    });
  };
  
  handleSave = () => {
    // Prepare data to save
    const { bubbles, connections } = this.state;
    
    // Call the onSave prop with the data
    if (this.props.onSave) {
      this.props.onSave({ bubbles, connections });
    }
    
    // Close the modal
    this.props.onClose();
  };
  

  
  // When mouse leaves the canvas, clear the temporary line
  handleCanvasMouseLeave = () => {
    if (this.state.isDrawingConnection) {
      this.setState({
        mouseX: 0,
        mouseY: 0
      });
    }
  };
  
  // Add this method to reset the drawing state completely
  resetDrawingState = () => {
    this.setState({
      startBubble: null,
      mouseX: 0,
      mouseY: 0
    });
  };
  
  // Modify the background click handler to reset drawing
  handleCanvasClick = (event) => {
    // If clicked directly on the canvas (not on a bubble)
    if (event.target === this.canvasRef.current) {
      this.resetDrawingState();
    }
  };
  
  toggleConnectionDrawing = () => {
    this.setState(prevState => ({
      isDrawingConnection: !prevState.isDrawingConnection,
      isDeletingConnection: false,
      // Always reset these when toggling drawing mode
      startBubble: null,
      mouseX: 0,
      mouseY: 0
    }));
  };
  
  toggleConnectionDeleting = () => {
    this.setState(prevState => ({
      isDeletingConnection: !prevState.isDeletingConnection,
      isDrawingConnection: false
    }));
  };
  
  renderConnections() {
    const { connections, bubbles, isDrawingConnection, startBubble, mouseX, mouseY } = this.state;
    
    return (
      <>
        {/* Render existing connections */}
        {connections.map((conn, index) => {
          const source = bubbles.find(b => b.roomName === conn.source);
          const target = bubbles.find(b => b.roomName === conn.target);
          
          if (!source || !target) return null;
          
          // Calculate midpoint for the clickable element
          const midX = (source.x + target.x) / 2;
          const midY = (source.y + target.y) / 2;
          
          return (
            <div key={`conn-container-${index}`}>
              {/* The actual SVG line */}
              <svg 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
              >
                <line
                  x1={source.x}
                  y1={source.y}
                  x2={target.x}
                  y2={target.y}
                  stroke={this.state.isDeletingConnection ? '#ff4444' : '#666'}
                  strokeWidth={2}
                />
              </svg>
              
              {/* Clickable element in the middle of the line */}
              {this.state.isDeletingConnection && (
                <div
                  style={{
                    position: 'absolute',
                    left: midX - 15,
                    top: midY - 15,
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 0, 0, 0.5)',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    zIndex: 100,
                  }}
                  onClick={() => this.handleConnectionClick(index)}
                >
                  ×
                </div>
              )}
            </div>
          );
        })}
        
        {/* Only render temporary connection line when actively creating a connection */}
        {isDrawingConnection && startBubble !== null && mouseX !== 0 && mouseY !== 0 && (
          <svg 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <line
              x1={bubbles.find(b => b.roomName === startBubble)?.x || 0}
              y1={bubbles.find(b => b.roomName === startBubble)?.y || 0}
              x2={mouseX}
              y2={mouseY}
              stroke="#666"
              strokeWidth={2}
              strokeDasharray="5,5"
            />
          </svg>
        )}
      </>
    );
  }
  
  renderBubbles() {
    const { bubbles } = this.state;
    const { isDrawingConnection, isDeletingConnection } = this.state;
    
    console.log('Rendering bubbles:', bubbles.length, bubbles);
    
    // If no bubbles, don't render anything
    if (bubbles.length === 0) {
      return null;
    }
    
    return bubbles.map(bubble => (
      <div
        key={bubble.id}
        style={{
          ...bubbleStyle,
          width: `${bubble.size}px`,
          height: `${bubble.size}px`,
          left: `${bubble.x - bubble.size / 2}px`,
          top: `${bubble.y - bubble.size / 2}px`,
          backgroundColor: bubble.color,
          cursor: isDrawingConnection ? 'pointer' : isDeletingConnection ? 'default' : 'move',
          border: this.state.startBubble === bubble.id ? '3px solid white' : 'none',
          boxShadow: this.state.startBubble === bubble.id ? '0 0 10px rgba(255,255,255,0.7)' : 'none',
        }}
        onMouseDown={(e) => this.handleMouseDown(e, bubble.id)}
        onMouseUp={() => this.handleBubbleMouseUp()}
        onClick={(e) => this.handleBubbleClick(e, bubble.roomName)}
      >
        {bubble.displayName}
      </div>
    ));
  }
  
  // Add a method to adjust the modal size based on content
  adjustModalSize = () => {
    const { bubbles } = this.state;
    if (!bubbles || bubbles.length === 0) return;
    
    // Find the furthest bubble
    let maxX = 0;
    let maxY = 0;
    
    bubbles.forEach(bubble => {
      maxX = Math.max(maxX, bubble.x + bubble.size/2);
      maxY = Math.max(maxY, bubble.y + bubble.size/2);
    });
    
    // Add some padding
    maxX += 50;
    maxY += 50;
    
    console.log('Adjusting canvas size to fit bubbles:', maxX, maxY);
    
    // Update the canvas container style if needed
    if (this.canvasRef.current) {
      // Set minimum width and height to ensure all bubbles are visible
      this.canvasRef.current.style.minWidth = `${maxX}px`;
      this.canvasRef.current.style.minHeight = `${maxY}px`;
    }
  };
  
  render() {
    const { show, onClose, translator } = this.props;
    const { isDrawingConnection, isDeletingConnection } = this.state;
    
    if (!show) return null;
    
    // Define styles inline to allow for dynamic adjustments
    const dynamicModalContentStyle = {
      ...modalContentStyle,
      width: '90%',       // Use percentage instead of fixed width
      maxWidth: '1000px', // But cap it at a reasonable size
      maxHeight: '90vh',  // Limit height for small screens
      display: 'flex',
      flexDirection: 'column',
    };
    
    return (
      <div style={modalStyle}>
        <div style={dynamicModalContentStyle}>
          <div style={headerStyle}>
            <h3 style={titleStyle}>{translator.t('Room Relationship Bubble Chart')}</h3>
            <button style={closeButtonStyle} onClick={onClose}>&times;</button>
          </div>
          
          <div 
            style={canvasContainerStyle} 
            ref={this.canvasRef}
            onMouseLeave={this.handleCanvasMouseLeave}
            onClick={this.handleCanvasClick}
            onMouseMove={this.handleMouseMove}
          >
            {this.renderConnections()}
            {this.renderBubbles()}
          </div>
          
          <div style={controlsStyle}>
            <button 
              style={{
                ...buttonStyle,
                backgroundColor: isDrawingConnection ? '#2c3e50' : SharedStyle.SECONDARY_COLOR.main
              }} 
              onClick={this.toggleConnectionDrawing}
            >
              {isDrawingConnection ? translator.t('Cancel Adding Connection') : translator.t('Add Connection')}
            </button>
            <button 
              style={{
                ...buttonStyle,
                backgroundColor: isDeletingConnection ? '#ff4444' : SharedStyle.SECONDARY_COLOR.main
              }} 
              onClick={this.toggleConnectionDeleting}
            >
              {isDeletingConnection ? translator.t('Cancel Deleting') : translator.t('Delete Connection')}
            </button>
            <button style={buttonStyle} onClick={this.handleSave}>
              {translator.t('Save')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

BubbleChartModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  roomCounts: PropTypes.object.isRequired,
  translator: PropTypes.object.isRequired,
  existingRelationships: PropTypes.shape({
    bubbles: PropTypes.array,
    connections: PropTypes.array
  })
};

BubbleChartModal.defaultProps = {
  existingRelationships: null
}; 